'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink, type LearningCourseDetail } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

/** Minimal shape of the i18next `t` so the pure string-building helpers
 *  ({@link priceFields}, {@link buildFeatures}) can localize without
 *  taking a hook dependency. */
type TFn = (key: string, options?: Record<string, unknown>) => string;

// PriceCard — three distinct sidebar designs.
//
// Earlier rounds shipped a single PriceCard with size variants
// (compact / normal / tall). That gave us padding differences but not
// real design differentiation. This round refactors into three
// meaningfully different layouts — one per page personality.
//
//   variant="standard"   Content-first
//                        Clean, informational, friendly.
//                        Preview thumb · price · curriculum chip ·
//                        CTA · favorite · feature list.
//
//   variant="premium"    Decision-first (Udemy-style marketplace)
//                        Persuasive, rich, dense. The page's
//                        primary commitment surface.
//                        Preview thumb · big price · CTA stack ·
//                        feature list · trust signals row ·
//                        coupon link.
//
//   variant="minimal"    Trust-first
//                        Quiet, refined, credibility-led.
//                        Compact · no preview · subtle price ·
//                        single quiet CTA · institution mark.
//
// All three read from the same `LearningCourseDetail` shape; they
// just choose different fields to emphasize.

interface PriceCardProps {
  course: LearningCourseDetail;
  tenantSlug: string;
  firstLessonId: string;
  variant?: 'standard' | 'premium' | 'minimal';
  /** Phase 5 — when present, replaces the hardcoded "Eğitime Başla"
   *  anchor with this node. The page route passes an EnrollmentIsland
   *  that decides per-user state (anon / not-enrolled / enrolled /
   *  completed) and produces the appropriate CTA. When omitted, the
   *  legacy hardcoded anchor renders — preserves backward compatibility
   *  with un-migrated call sites. */
  enrollAction?: ReactNode;
}

export function PriceCard({
  course,
  tenantSlug,
  firstLessonId,
  variant = 'standard',
  enrollAction,
}: PriceCardProps) {
  const props = { course, tenantSlug, firstLessonId, enrollAction };
  if (variant === 'premium') return <PriceCardPremium {...props} />;
  if (variant === 'minimal') return <PriceCardMinimal {...props} />;
  return <PriceCardStandard {...props} />;
}

// ---------------------------------------------------------------------------
// Standard — Content-first
// ---------------------------------------------------------------------------
//
// Friendly, informational. Curriculum scope shown as a chip. Feature
// list visible but quiet. Single primary CTA.

function PriceCardStandard({ course, tenantSlug, firstLessonId, enrollAction }: PriceCardCommon) {
  const { t } = useTranslation('common');
  const { isFree, priceDisplay, playHref, totalLessons } = priceFields(
    course,
    tenantSlug,
    firstLessonId,
    t,
  );
  const features = buildFeatures(course, t).slice(0, 4);

  return (
    <Card asChild>
      <div className="cd-pricecard cd-pricecard-standard">
        <PortalLink
          className="cd-pricecard-preview"
          href={playHref}
          aria-label={t('label.preview')}
        >
          {course.imageUrl ? (
            <img
              src={course.imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              width={320}
              height={180}
            />
          ) : (
            <div className="cd-pricecard-preview-poster" aria-hidden>
              ▶
            </div>
          )}
          <div className="cd-pricecard-preview-play" aria-hidden>
            ▶
          </div>
          <div className="cd-pricecard-preview-label">{t('label.preview')}</div>
        </PortalLink>

        <div className="cd-pricecard-body">
          <div className="cd-pricecard-kicker">
            {isFree ? t('label.freeAccess') : t('label.oneTime')}
          </div>
          <div className={`cd-pricecard-price${isFree ? ' cd-pricecard-price-free' : ''}`}>
            {priceDisplay}
          </div>

          {/* Standard's signature: curriculum chip below the price.
            Reflects the Content-first emphasis on what's inside. */}
          {(course.chapters?.length ?? 0) > 0 && (
            <div className="cd-pricecard-scope-chip">
              <span aria-hidden>📚</span>
              <span>
                {t('label.chaptersCount', { count: course.chapters?.length ?? 0 })} ·{' '}
                {t('label.lessonsCount', { count: totalLessons })}
              </span>
              {course.duration && <span aria-hidden>·</span>}
              {course.duration && <span>{course.duration}</span>}
            </div>
          )}

          {enrollAction ?? (
            <PortalLink className="cd-pricecard-cta" href={playHref}>
              {isFree ? t('button.startCourse') : t('label.startProgram')}
              <span aria-hidden>→</span>
            </PortalLink>
          )}
          <button className="cd-pricecard-cta-secondary" type="button">
            <span aria-hidden>♡</span>
            {t('label.addToFavorites')}
          </button>

          {features.length > 0 && (
            <ul className="cd-pricecard-features">
              {features.map((f, i) => (
                <li key={i} className="cd-pricecard-feature">
                  <span className="cd-pricecard-feature-icon" aria-hidden>
                    ✓
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Premium — Decision-first (Udemy-style marketplace)
// ---------------------------------------------------------------------------
//
// The page's primary commitment surface. Borrows from Udemy's
// info-density: preview thumb, big confident price, primary CTA,
// secondary CTA, divider, what's-included list, divider, trust
// signals row, coupon code link. Every section earns its place.

function PriceCardPremium({ course, tenantSlug, firstLessonId, enrollAction }: PriceCardCommon) {
  const { t } = useTranslation('common');
  const { isFree, priceDisplay, playHref } = priceFields(course, tenantSlug, firstLessonId, t);
  const features = buildFeatures(course, t);

  return (
    <Card asChild>
      <div className="cd-pricecard cd-pricecard-premium">
        <PortalLink
          className="cd-pricecard-preview"
          href={playHref}
          aria-label={t('label.preview')}
        >
          {course.imageUrl ? (
            <img
              src={course.imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              width={320}
              height={180}
            />
          ) : (
            <div className="cd-pricecard-preview-poster" aria-hidden>
              ▶
            </div>
          )}
          <div className="cd-pricecard-preview-play cd-pricecard-preview-play-lg" aria-hidden>
            ▶
          </div>
          <div className="cd-pricecard-preview-label">
            {t('label.preview')} · {t('label.free').trim()}
          </div>
        </PortalLink>

        <div className="cd-pricecard-body">
          <div className="cd-pricecard-kicker">
            {isFree ? t('label.freeAccess') : t('label.oneTimePayment')}
          </div>
          <div
            className={`cd-pricecard-price cd-pricecard-price-lg${isFree ? ' cd-pricecard-price-free' : ''}`}
          >
            {priceDisplay}
          </div>
          <div className="cd-pricecard-sub">
            {isFree ? t('label.freeCourseTagline') : t('label.paidCourseTagline')}
          </div>

          {enrollAction ?? (
            <PortalLink className="cd-pricecard-cta cd-pricecard-cta-lg" href={playHref}>
              {isFree ? t('button.startCourse') : t('label.startProgram')}
              <span aria-hidden>→</span>
            </PortalLink>
          )}
          <button className="cd-pricecard-cta-secondary" type="button">
            <span aria-hidden>♡</span>
            {t('label.addToFavorites')}
          </button>

          {features.length > 0 && (
            <div className="cd-pricecard-section">
              <div className="cd-pricecard-section-label">{t('label.includedInCourse')}</div>
              <ul className="cd-pricecard-features cd-pricecard-features-tight">
                {features.map((f, i) => (
                  <li key={i} className="cd-pricecard-feature">
                    <span className="cd-pricecard-feature-icon" aria-hidden>
                      ✓
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trust signals — vary by paid/free. A 30-day refund row
            on a free course reads as nonsense; instead we surface
            "instant access" / "all lessons unlocked" / "lifetime"
            for free courses. */}
          <div className="cd-pricecard-section">
            <ul className="cd-pricecard-trust-grid">
              {!isFree && (
                <li>
                  <span className="cd-pricecard-trust-iconwell" aria-hidden>
                    ↺
                  </span>
                  <span>{t('label.refundGuarantee')}</span>
                </li>
              )}
              {isFree && (
                <li>
                  <span className="cd-pricecard-trust-iconwell" aria-hidden>
                    ⚡
                  </span>
                  <span>{t('label.instantAccess')}</span>
                </li>
              )}
              {course.hasLifetimeAccess && (
                <li>
                  <span className="cd-pricecard-trust-iconwell" aria-hidden>
                    ∞
                  </span>
                  <span>{t('label.lifetimeAccess')}</span>
                </li>
              )}
              {course.hasMobileAccess && (
                <li>
                  <span className="cd-pricecard-trust-iconwell" aria-hidden>
                    ◆
                  </span>
                  <span>{t('label.useOnAllDevices')}</span>
                </li>
              )}
              {course.hasCertificate && (
                <li>
                  <span className="cd-pricecard-trust-iconwell" aria-hidden>
                    ★
                  </span>
                  <span>{t('label.certificateIncluded')}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Coupon only makes sense for paid courses. Free courses
            have nothing to discount; the field would be cargo-cult. */}
          {!isFree && (
            <details className="cd-pricecard-coupon">
              <summary>{t('label.applyCoupon')}</summary>
              <div className="cd-pricecard-coupon-body">
                <input
                  type="text"
                  placeholder={t('label.couponCode')}
                  className="cd-pricecard-coupon-input"
                  aria-label={t('label.couponCode')}
                />
                <button type="button" className="cd-pricecard-coupon-button">
                  {t('label.apply')}
                </button>
              </div>
            </details>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Minimal — Trust-first
// ---------------------------------------------------------------------------
//
// Quiet, refined. No preview thumb — the instructor card in the hero
// IS the visual on Trust-first pages. The card sits as a calm
// supporter, not a commerce primary surface.

function PriceCardMinimal({ course, tenantSlug, firstLessonId, enrollAction }: PriceCardCommon) {
  const { t } = useTranslation('common');
  const { isFree, priceDisplay, playHref } = priceFields(course, tenantSlug, firstLessonId, t);
  const features = buildFeatures(course, t).slice(0, 3);

  return (
    <Card asChild>
      <div className="cd-pricecard cd-pricecard-minimal">
        <div className="cd-pricecard-body">
          <div className="cd-pricecard-minimal-mark">
            {course.categoryName ?? t('title.academy')}
          </div>
          <div
            className={`cd-pricecard-price cd-pricecard-price-sm${isFree ? ' cd-pricecard-price-free' : ''}`}
          >
            {priceDisplay}
          </div>
          <div className="cd-pricecard-sub">
            {isFree ? t('label.freeCourseTaglineShort') : t('label.paidCourseTaglineShort')}
          </div>

          {enrollAction ?? (
            <PortalLink className="cd-pricecard-cta cd-pricecard-cta-outline" href={playHref}>
              {isFree ? t('button.startCourse') : t('label.startProgram')}
              <span aria-hidden>→</span>
            </PortalLink>
          )}

          {features.length > 0 && (
            <ul className="cd-pricecard-features cd-pricecard-features-quiet">
              {features.map((f, i) => (
                <li key={i} className="cd-pricecard-feature">
                  <span className="cd-pricecard-feature-dot" aria-hidden>
                    ·
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

interface PriceCardCommon {
  course: LearningCourseDetail;
  tenantSlug: string;
  firstLessonId: string;
  enrollAction?: ReactNode;
}

function priceFields(
  course: LearningCourseDetail,
  tenantSlug: string,
  firstLessonId: string,
  t: TFn,
) {
  const isFree = course.isFree || !course.price;
  const priceDisplay = isFree
    ? t('label.free').trim()
    : formatPrice(course.price ?? 0, course.priceCurrency ?? 'TRY');
  const playHref = `/${tenantSlug}/learn/${course.id}/play/${firstLessonId}`;
  const totalLessons = course.chapters?.reduce((s, ch) => s + (ch.lessons?.length ?? 0), 0) ?? 0;
  return { isFree, priceDisplay, playHref, totalLessons };
}

function buildFeatures(course: LearningCourseDetail, t: TFn): string[] {
  const features: string[] = [];
  if (course.duration) features.push(t('label.contentDuration', { duration: course.duration }));
  if (course.totalLectures) features.push(t('label.lessonsCount', { count: course.totalLectures }));
  if (course.hasMobileAccess) features.push(t('label.mobileAccess'));
  if (course.hasLifetimeAccess) features.push(t('label.lifetimeAccess'));
  if (course.hasCertificate) features.push(t('label.completionCertificate'));
  if (course.totalDownloads)
    features.push(t('label.downloadableResources', { count: course.totalDownloads }));
  return features;
}

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
